import { Minus, Plus } from "lucide-react";
import type { ComponentProps, ReactNode, Ref } from "react";
import { useEffect, useImperativeHandle, useRef } from "react";
import type { UseFormReturn } from "react-hook-form";
import { useWatch } from "react-hook-form";

import type { Schema } from "../../model";
import { zSchema } from "../../model";

import { getDefaultValues, useCalculatorForm } from "./utils";

import type { ServiceEntity } from "~/entities/service";
import { sum } from "~/shared/arr";
import { formatCurrency, useTranslation2 } from "~/shared/i18n";
import { cn } from "~/shared/ui";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "~/shared/ui/accordion";
import { Button } from "~/shared/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "~/shared/ui/form";
import { Input } from "~/shared/ui/input";

type Handle = {
  formElement: HTMLFormElement;
  hookForm: UseFormReturn<Schema>;
} | null;

type Props = Omit<ComponentProps<"form">, "ref" | "onSubmit"> & {
  ref?: Ref<Handle> | undefined;
  form?: UseFormReturn<Schema, unknown, Schema> | undefined;
  orderedServices: Parameters<typeof getDefaultValues>[0];
  services: ServiceEntity.Schema[];
  moreInfoButton: (service: ServiceEntity.Schema) => ReactNode;
  onSubmit?: Parameters<UseFormReturn<Schema, unknown, Schema>["handleSubmit"]>[0] | undefined;
};

function CalculatorForm({
  ref,
  form,
  orderedServices,
  services,
  moreInfoButton,
  onSubmit,
  className,
  ...props
}: Props) {
  const {
    translation: { t },
  } = useTranslation2();

  const [groupedServices, form_] = useCalculatorForm(orderedServices, services, t);
  const formForUse = form ?? form_;

  useWatch({
    control: formForUse.control,
  });

  useEffect(() => {
    formForUse.reset(getDefaultValues(orderedServices, groupedServices));
  }, [formForUse, groupedServices, orderedServices]);

  const formRef = useRef<HTMLFormElement>(null);

  useImperativeHandle<Handle, Handle>(ref, () => {
    const formElement = formRef.current;
    if (!formElement) return null;

    return {
      formElement,
      hookForm: formForUse,
    };
  }, [formForUse]);

  const handleSubmit: ComponentProps<"form">["onSubmit"] = (event) => {
    if (!onSubmit) return;

    void formForUse.handleSubmit(onSubmit)(event);
  };

  return (
    <Form {...formForUse}>
      <form
        ref={formRef}
        className={cn("flex flex-col", className)}
        onSubmit={handleSubmit}
        {...props}
      >
        <Accordion type="multiple">
          {Object.entries(groupedServices).map(([categoryId, services]) => {
            if (!services) throw new Error("Services must be present");

            const categoryName = services.find((service) => service.category.id === categoryId)
              ?.category.name;

            return (
              <AccordionItem key={categoryId} value={categoryId}>
                <AccordionTrigger className="items-center text-base">
                  <span className="mr-auto">{categoryName}</span>
                  <span className="order-1 font-semibold">
                    {formatCurrency(
                      calculateCostOfServices(formForUse.getValues()[categoryId] ?? [], services),
                    )}
                  </span>
                </AccordionTrigger>
                <AccordionContent className="flex flex-col gap-4">
                  {services.map((service, index) => {
                    const { id, name, price } = service;

                    return (
                      <FormField
                        key={id}
                        control={formForUse.control}
                        name={`${categoryId}.${index}.count`}
                        render={({ field: { value, onChange } }) => {
                          const buttonProps: ComponentProps<typeof Button> = {
                            type: "button",
                            variant: "ghost",
                            size: "icon",
                          };
                          const changeValue = (change: (valueAsNumber: number) => void) => {
                            const valueAsNumber = Number(value);
                            if (Number.isInteger(valueAsNumber)) {
                              change(valueAsNumber);
                            }
                          };

                          return (
                            <FormItem>
                              <FormLabel className={cn("font-normal")}>
                                {name}
                                {moreInfoButton(service)}
                                <span className="ml-auto">{formatCurrency(price)}</span>
                              </FormLabel>
                              <div className="flex items-center gap-2">
                                <Button
                                  disabled={value <= 0}
                                  onClick={() =>
                                    changeValue((valueAsNumber) =>
                                      onChange(valueAsNumber > 0 ? valueAsNumber - 1 : 0),
                                    )
                                  }
                                  {...buttonProps}
                                >
                                  <Minus />
                                </Button>
                                <FormControl>
                                  <Input
                                    type="number"
                                    min={
                                      zSchema.valueSchema.element.shape.count.minValue ?? undefined
                                    }
                                    className="input-without-arrows w-32 text-center"
                                    value={value}
                                    onChange={(event) => {
                                      const value = Number(event.target.valueAsNumber);
                                      if (isNaN(value)) return;

                                      onChange(value);
                                    }}
                                  />
                                </FormControl>
                                <Button
                                  onClick={() =>
                                    changeValue((valueAsNumber) =>
                                      onChange(valueAsNumber > 0 ? valueAsNumber + 1 : 1),
                                    )
                                  }
                                  {...buttonProps}
                                >
                                  <Plus />
                                </Button>
                                <span className="ml-auto">
                                  {formatCurrency((price ?? 0) * (value >= 0 ? value : 0))}
                                </span>
                              </div>
                              <FormMessage />
                            </FormItem>
                          );
                        }}
                      />
                    );
                  })}
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      </form>
    </Form>
  );
}

function calculateCostOfServices(
  formServices: Schema[keyof Schema],
  services: ServiceEntity.Schema[],
) {
  return sum(
    formServices.map(
      ({ id, count }) => (services.find(({ id: id_ }) => id_ === id)?.price ?? 0) * count,
    ),
  );
}

export { CalculatorForm };
export type { Handle as CalculatorFormHandle };
